import React from 'react';
import Icon from '../../../components/AppIcon';

const UserAnalytics = ({ users }) => {
  // Calculate role distribution
  const roleDistribution = {
    gym_owner: users?.filter(u => u?.role === 'gym_owner')?.length || 0,
    trainer: users?.filter(u => u?.role === 'trainer')?.length || 0,
    staff: users?.filter(u => u?.role === 'staff')?.length || 0,
    member: users?.filter(u => u?.role === 'member')?.length || 0
  };

  // Calculate membership plan distribution
  const planDistribution = {
    basic: users?.filter(u => u?.membership_plan === 'basic')?.length || 0,
    premium: users?.filter(u => u?.membership_plan === 'premium')?.length || 0,
    elite: users?.filter(u => u?.membership_plan === 'elite')?.length || 0
  };

  // Recent account creations (last 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
  const recentAccounts = users?.filter(u => new Date(u?.joined_at) >= sevenDaysAgo)?.length || 0;

  return (
    <div className="space-y-6">
      {/* Role Distribution */}
      <div className="bg-card rounded-xl shadow-md p-6">
        <h3 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="PieChart" size={20} color="var(--color-primary)" />
          Role Distribution
        </h3>
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Gym Owners</span>
              <span className="text-sm font-semibold text-foreground">{roleDistribution?.gym_owner}</span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-smooth"
                style={{ width: `${(roleDistribution?.gym_owner / users?.length) * 100 || 0}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Trainers</span>
              <span className="text-sm font-semibold text-foreground">{roleDistribution?.trainer}</span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-smooth"
                style={{ width: `${(roleDistribution?.trainer / users?.length) * 100 || 0}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Staff</span>
              <span className="text-sm font-semibold text-foreground">{roleDistribution?.staff}</span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-warning transition-smooth"
                style={{ width: `${(roleDistribution?.staff / users?.length) * 100 || 0}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Members</span>
              <span className="text-sm font-semibold text-foreground">{roleDistribution?.member}</span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-success transition-smooth"
                style={{ width: `${(roleDistribution?.member / users?.length) * 100 || 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Membership Plans */}
      <div className="bg-card rounded-xl shadow-md p-6">
        <h3 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="CreditCard" size={20} color="var(--color-primary)" />
          Membership Plans
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-background rounded-lg">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-muted" />
              <span className="text-sm text-foreground">Basic</span>
            </div>
            <span className="text-sm font-semibold text-foreground">{planDistribution?.basic}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-background rounded-lg">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-primary" />
              <span className="text-sm text-foreground">Premium</span>
            </div>
            <span className="text-sm font-semibold text-foreground">{planDistribution?.premium}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-background rounded-lg">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-accent" />
              <span className="text-sm text-foreground">Elite</span>
            </div>
            <span className="text-sm font-semibold text-foreground">{planDistribution?.elite}</span>
          </div>
        </div>
      </div>
      {/* Account Activity */}
      <div className="bg-card rounded-xl shadow-md p-6">
        <h3 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          Account Activity
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-background rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="UserPlus" size={20} color="var(--color-success)" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">New Accounts</p>
                <p className="text-xs text-muted-foreground">Last 7 days</p>
              </div>
            </div>
            <span className="text-xl font-bold text-foreground">{recentAccounts}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-background rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Users" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Total Users</p>
                <p className="text-xs text-muted-foreground">All accounts</p>
              </div>
            </div>
            <span className="text-xl font-bold text-foreground">{users?.length || 0}</span>
          </div>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="bg-card rounded-xl shadow-md p-6">
        <h3 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart3" size={20} color="var(--color-primary)" />
          Quick Stats
        </h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Active Rate</span>
            <span className="font-semibold text-foreground">
              {users?.length > 0 ? Math.round((users?.filter(u => u?.is_active)?.length / users?.length) * 100) : 0}%
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Staff to Member Ratio</span>
            <span className="font-semibold text-foreground">
              1:{roleDistribution?.member > 0 ? Math.round(roleDistribution?.member / (roleDistribution?.staff + roleDistribution?.trainer || 1)) : 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;